import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LifeBuoy, Search, Plus, Filter, SlidersHorizontal } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  DataTable,
  StatusPill,
  EmptyState,
  Pagination,
  FilterBuilder,
  type ColumnDef,
  type FilterCondition,
} from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Badge,
  Skeleton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import {
  useTickets,
  useTicketTypes,
  type TicketMinimal,
  type TicketStatus,
  type TicketPriority,
} from '@/api/support';
import {
  TICKET_STATUS_TONE,
  TICKET_STATUS_LABEL,
  TICKET_PRIORITY_TONE,
  TICKET_PRIORITY_LABEL,
  ticketTypeLabel,
  formatDate,
} from './shared';
import { CreateTicketDialog } from './components/CreateTicketDialog';
import { TicketStatBand } from './components/TicketStatBand';

const PAGE_SIZE = 20;

// Columns the user can show/hide from "Display options".
const TOGGLEABLE: { id: string; label: string }[] = [
  { id: 'priority', label: 'Priority' },
  { id: 'service', label: 'Service' },
  { id: 'requested', label: 'Opened' },
];

// Enum choices offered as multiselect values in the Filters builder.
const STATUS_FILTER_OPTIONS = (Object.keys(TICKET_STATUS_LABEL) as TicketStatus[]).map(
  (s) => ({ value: s, label: TICKET_STATUS_LABEL[s] }),
);
const PRIORITY_FILTER_OPTIONS = (Object.keys(TICKET_PRIORITY_LABEL) as TicketPriority[]).map(
  (p) => ({ value: p, label: TICKET_PRIORITY_LABEL[p] }),
);

const ENUM_OPERATORS = ['is', 'is_not', 'is_any_of', 'is_none_of'];

// Default view: only active tickets (hide completed / closed / cancelled).
const DEFAULT_STATUS_FILTER: TicketStatus[] = ['opening', 'work-in-progress', 'pending'];
const DEFAULT_FILTERS: FilterCondition[] = [
  {
    id: 'default-status',
    field: 'status',
    operator: 'is_any_of',
    value: DEFAULT_STATUS_FILTER.join(','),
  },
];

/** Canonical (raw) value of a field, for enum comparisons. */
function fieldRaw(t: TicketMinimal, field: string): string {
  switch (field) {
    case 'status':
      return t.ticket_status;
    case 'priority':
      return t.ticket_priority;
    case 'service':
      return t.service_name ?? '';
    case 'type':
      return t.ticket_type_id != null ? String(t.ticket_type_id) : '';
    default:
      return '';
  }
}

/** Display text of a field, for free-text operators. */
function fieldText(t: TicketMinimal, field: string): string {
  switch (field) {
    case 'status':
      return `${TICKET_STATUS_LABEL[t.ticket_status]} ${t.ticket_status}`;
    case 'priority':
      return `${TICKET_PRIORITY_LABEL[t.ticket_priority]} ${t.ticket_priority}`;
    default:
      return fieldRaw(t, field);
  }
}

function matchCondition(t: TicketMinimal, c: FilterCondition): boolean {
  const v = c.value.trim();
  if (!v) return true; // empty value → condition ignored
  const list = v.toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);

  // Enum operators compare against the raw field value (status/priority codes).
  if (ENUM_OPERATORS.includes(c.operator)) {
    const raw = fieldRaw(t, c.field).toLowerCase();
    switch (c.operator) {
      case 'is':
        return raw === list[0];
      case 'is_not':
        return raw !== list[0];
      case 'is_any_of':
        return list.includes(raw);
      case 'is_none_of':
        return !list.includes(raw);
      default:
        return true;
    }
  }

  // Free-text operators.
  const hay = fieldText(t, c.field).toLowerCase();
  const needle = v.toLowerCase();
  const tokens = hay.split(/\s+/);
  switch (c.operator) {
    case 'contains':
      return hay.includes(needle);
    case 'not_contains':
      return !hay.includes(needle);
    case 'equals':
      return hay === needle || tokens.includes(needle);
    case 'not_equals':
      return !(hay === needle || tokens.includes(needle));
    case 'starts_with':
      return hay.startsWith(needle);
    case 'ends_with':
      return hay.endsWith(needle);
    default:
      return true;
  }
}

export function TicketListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<TicketStatus | ''>('');
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterCondition[]>(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const activeFilters = filters.filter((f) => f.value.trim()).length;
  const [visible, setVisible] = useState<Record<string, boolean>>({
    priority: true,
    service: true,
    requested: true,
  });
  const createOpen = searchParams.has('new');

  const setCreateOpen = (open: boolean) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (open) next.set('new', '1');
        else next.delete('new');
        return next;
      },
      { replace: true },
    );
  };

  const { data, isLoading, isError } = useTickets({ page, pageSize: PAGE_SIZE, status });

  // Ticket-type choices for the Filters "Type" field (value = id, label = English).
  const { data: ticketTypes = [] } = useTicketTypes();
  const typeOptions = useMemo(
    () => ticketTypes.map((t) => ({ value: String(t.ticket_type_id), label: ticketTypeLabel(t) })),
    [ticketTypes],
  );

  const rows = useMemo(() => {
    let all = data?.results ?? [];
    const active = filters.filter((f) => f.value.trim());
    if (active.length) {
      all = all.filter((t) => active.every((c) => matchCondition(t, c)));
    }
    const q = query.trim().toLowerCase();
    if (q) {
      all = all.filter(
        (t) =>
          t.ticket_content.toLowerCase().includes(q) ||
          String(t.ticket_id).includes(q),
      );
    }
    return all;
  }, [data, query, filters]);

  const columns = useMemo<ColumnDef<TicketMinimal, unknown>[]>(
    () => [
      {
        id: 'ticket_id',
        header: '#',
        accessorKey: 'ticket_id',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>
            {row.original.ticket_id}
          </span>
        ),
      },
      {
        id: 'subject',
        header: 'Subject',
        cell: ({ row }) => (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--vn-space-2)',
              fontWeight: 'var(--vn-font-weight-medium)',
              maxWidth: 360,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.ticket_content}
          >
            {row.original.ticket_content || '(no description)'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const s = row.original.ticket_status;
          return (
            <StatusPill tone={TICKET_STATUS_TONE[s]} loading={s === 'work-in-progress'}>
              {TICKET_STATUS_LABEL[s]}
            </StatusPill>
          );
        },
      },
      {
        id: 'priority',
        header: 'Priority',
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original.ticket_priority;
          return (
            <Badge tone={TICKET_PRIORITY_TONE[p]} appearance="outline" size="sm" dot>
              {TICKET_PRIORITY_LABEL[p]}
            </Badge>
          );
        },
      },
      {
        id: 'service',
        header: 'Service',
        cell: ({ row }) =>
          row.original.service_name ? (
            row.original.service_name
          ) : (
            <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>
          ),
      },
      {
        id: 'requested',
        header: 'Opened',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)' }}>
            {formatDate(row.original.ticket_requested_at || row.original.created_at)}
          </span>
        ),
      },
      {
        id: 'view',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>
            <Button variant="ghost" size="sm" onClick={() => navigate(`/support/${row.original.ticket_id}`)}>
              View
            </Button>
          </div>
        ),
      },
    ],
    [navigate],
  );

  // Hide any toggleable column the user switched off in "Display options".
  const visibleColumns = useMemo(
    () => columns.filter((c) => !(c.id && c.id in visible) || visible[c.id]),
    [columns, visible],
  );

  const pagination = data?.pagination;

  return (
    <Container size="xl">
      <PageHeader
        icon={<LifeBuoy size={22} />}
        title="Support"
        description="Track and raise support requests for your services."
        actions={
          <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
            New ticket
          </Button>
        }
      />

      <TicketStatBand
        active={status}
        onSelect={(s) => {
          setStatus(s);
          setPage(1);
        }}
      />

      <Toolbar
        start={
          <Input
            placeholder="Search tickets"
            startSlot={<Search />}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ maxWidth: 360 }}
          />
        }
        end={
          <>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters
                  {activeFilters ? (
                    <Badge tone="primary" appearance="subtle" size="sm" style={{ marginLeft: 6 }}>
                      {activeFilters}
                    </Badge>
                  ) : null}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={[
                    {
                      value: 'status',
                      label: 'Status',
                      input: 'multiselect',
                      options: STATUS_FILTER_OPTIONS,
                    },
                    {
                      value: 'priority',
                      label: 'Priority',
                      input: 'multiselect',
                      options: PRIORITY_FILTER_OPTIONS,
                    },
                    { value: 'service', label: 'Service' },
                    {
                      value: 'type',
                      label: 'Type',
                      ...(typeOptions.length
                        ? { input: 'multiselect' as const, options: typeOptions }
                        : {}),
                    },
                  ]}
                  defaultValue={filters.length ? filters : undefined}
                  onClose={() => setFilterOpen(false)}
                  onApply={(conditions) => {
                    setFilters(conditions);
                    setPage(1);
                    setFilterOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {TOGGLEABLE.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.id}
                    checked={visible[c.id]}
                    onCheckedChange={(v) =>
                      setVisible((prev) => ({ ...prev, [c.id]: v === true }))
                    }
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 44 }} />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={<LifeBuoy size={24} />}
          title="Couldn't load tickets"
          description="Something went wrong fetching your support tickets. Please try again."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<LifeBuoy size={24} />}
          title={query || status || activeFilters ? 'No tickets match your filters' : 'No tickets yet'}
          description={
            query || status || activeFilters
              ? 'Try clearing the search or filters.'
              : 'When you raise a support request it will appear here.'
          }
          actions={
            !query && !status && !activeFilters ? (
              <Button startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
                New ticket
              </Button>
            ) : undefined
          }
        />
      ) : (
        /* Constrains the DataTable to the Container width on narrow viewports so
           its internal overflow-x:auto scroll triggers instead of pushing the page wide. */
        <div style={{ minWidth: 0, maxWidth: '100%' }}>
          <DataTable
            columns={visibleColumns}
            data={rows}
            getRowId={(r) => String(r.ticket_id)}
            onRowClick={(r) => navigate(`/support/${r.ticket_id}`)}
            footer={
              pagination && pagination.total_pages > 1 ? (
                <Pagination
                  page={pagination.page}
                  pageCount={pagination.total_pages}
                  onPageChange={setPage}
                  summary={`Showing ${rows.length} of ${pagination.total} tickets`}
                />
              ) : (
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-md)' }}>
                  {`${pagination?.total ?? rows.length} ticket${(pagination?.total ?? rows.length) === 1 ? '' : 's'}`}
                </span>
              )
            }
          />
        </div>
      )}

      <CreateTicketDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/support/${id}`)}
      />
    </Container>
  );
}
